<template>
  <b-container fluid class="notification-recipient-page py-3">
    <div class="iq-card">
      <div class="iq-card-header d-flex align-items-center justify-content-between">
        <div class="iq-header-title py-2">
          <h4 class="card-title mb-0">Notification Recipient</h4>
        </div>
        <div class="header-actions">
          <b-button variant="outline-secondary" size="sm" :to="{ name: 'MasterConfiguration' }">
            Back
          </b-button>
          <b-button variant="primary" size="sm" :disabled="isLoading" @click="fetchList">
            <i v-if="isLoading && activeMethod === 'GET'" class="ri-loader-line mr-1"></i>
            Refresh
          </b-button>
        </div>
      </div>

      <div class="iq-card-body">
        <b-alert v-if="message.text" show :variant="message.variant" class="mb-3">
          {{ message.text }}
        </b-alert>

        <b-row>
          <b-col lg="12" class="mb-3">
            <section class="panel">
              <div class="table-toolbar">
                <div>
                  <h5 class="panel-title mb-1">Recipients</h5>
                  <small class="text-muted">{{ totalData }} data</small>
                </div>
                <div class="table-actions">
                  <b-button variant="success" size="sm" @click="openAddRecipientModal">
                    Add Recipient
                  </b-button>
                  <div class="pagination-control">
                    <b-form-select
                      v-model="pagination.itemPerPage"
                      :options="pageSizeOptions"
                      size="sm"
                      :disabled="isLoading"
                      @change="onPageSizeChanged"
                    />
                  </div>
                </div>
              </div>

              <b-row class="mb-3">
                <b-col md="6" class="mb-2 mb-md-0">
                  <b-input-group size="sm">
                    <template #prepend>
                      <b-input-group-text>
                        <i class="ri-user-line"></i>
                      </b-input-group-text>
                    </template>
                    <b-form-input
                      v-model.trim="searchName"
                      type="text"
                      placeholder="Search name..."
                    />
                    <template #append>
                      <b-button
                        v-if="searchName"
                        variant="outline-secondary"
                        @click="searchName = ''"
                      >
                        <i class="ri-close-line"></i>
                      </b-button>
                    </template>
                  </b-input-group>
                </b-col>

                <b-col md="6">
                  <b-input-group size="sm">
                    <template #prepend>
                      <b-input-group-text>
                        <i class="ri-mail-line"></i>
                      </b-input-group-text>
                    </template>
                    <b-form-input
                      v-model.trim="searchEmail"
                      type="text"
                      placeholder="Search email..."
                    />
                    <template #append>
                      <b-button
                        v-if="searchEmail"
                        variant="outline-secondary"
                        @click="searchEmail = ''"
                      >
                        <i class="ri-close-line"></i>
                      </b-button>
                    </template>
                  </b-input-group>
                </b-col>
              </b-row>

              <b-table
                responsive
                hover
                bordered
                small
                :busy="isLoading && activeMethod === 'GET'"
                :items="items"
                :fields="tableFields"
                show-empty
                :empty-text="emptyText"
              >
                <template #table-busy>
                  <div class="text-center py-4">
                    <b-spinner small class="mr-2"></b-spinner>
                    Loading...
                  </div>
                </template>

                <template #cell(ntrcStatus)="row">
                  <b-badge :variant="row.item.ntrcStatus === 1 ? 'success' : 'secondary'">
                    {{ row.item.ntrcStatus === 1 ? 'Active' : 'Inactive' }}
                  </b-badge>
                </template>

                <template #cell(actions)="row">
                  <div class="row-actions">
                    <b-button
                      variant="outline-primary"
                      size="sm"
                      :disabled="isLoading"
                      @click="openEditRecipientModal(row.item)"
                    >
                      Edit
                    </b-button>
                    <b-button
                      variant="outline-danger"
                      size="sm"
                      :disabled="isLoading"
                      @click="confirmDelete(row.item)"
                    >
                      Delete
                    </b-button>
                  </div>
                </template>
              </b-table>

              <div class="d-flex justify-content-end mt-3">
                <b-pagination
                  v-model="pagination.page"
                  :total-rows="pagination.totalData"
                  :per-page="pagination.itemPerPage"
                  size="sm"
                  :disabled="isLoading"
                  @change="onPageChanged"
                />
              </div>
            </section>
          </b-col>
        </b-row>
      </div>
    </div>

    <b-modal
      v-model="deleteModalVisible"
      title="Delete recipient"
      ok-title="Delete"
      ok-variant="danger"
      :ok-disabled="isLoading"
      :cancel-disabled="isLoading"
      @ok.prevent="deleteRecipient"
    >
      <p class="mb-0">
        Delete <strong>{{ selectedRecipient.ntrcName }}</strong>
        <span v-if="selectedRecipient.ntrcEmail">({{ selectedRecipient.ntrcEmail }})</span>?
      </p>
    </b-modal>

    <b-modal
      v-model="addModalVisible"
      :title="isEditing ? 'Edit notification recipient' : 'Add notification recipient'"
      hide-footer
      no-close-on-backdrop
      no-close-on-esc
    >
      <b-form @submit.prevent="createRecipient">
        <b-form-group v-if="!isEditing" label="Pick from master users">
          <searchable-select
            v-model="selectedUsers"
            :fetcher="searchMasterUsers"
            placeholder="Search user (email/username)"
            :disabled="isLoading"
            multiple
          />
          <small class="text-muted">You can select multiple users.</small>
        </b-form-group>

        <b-alert v-if="!isEditing && selectedUsers.length" show variant="info" class="mb-3">
          {{ selectedUsers.length }} user(s) selected. Name and email fields are disabled.
        </b-alert>

        <b-form-group label="Name" label-for="ntrc-name">
          <b-form-input
            id="ntrc-name"
            v-model.trim="form.ntrcName"
            placeholder="Recipient name"
            :disabled="isLoading || (!isEditing && selectedUsers.length > 0)"
          />
        </b-form-group>

        <b-form-group label="Email" label-for="ntrc-email">
          <b-form-input
            id="ntrc-email"
            v-model.trim="form.ntrcEmail"
            type="email"
            placeholder="name@example.com"
            :disabled="isLoading || (!isEditing && selectedUsers.length > 0)"
          />
        </b-form-group>

        <b-form-group label="Status" label-for="ntrc-status">
          <b-form-select
            id="ntrc-status"
            v-model="form.ntrcStatus"
            :options="statusOptions"
            :disabled="isLoading"
          />
        </b-form-group>

        <div class="modal-actions">
          <b-button variant="outline-secondary" :disabled="isLoading" @click="closeAddRecipientModal">
            Cancel
          </b-button>
          <b-button type="submit" variant="success" :disabled="isLoading || !canSubmit">
            <i v-if="isLoading && activeMethod === 'POST'" class="ri-loader-line mr-1"></i>
            Save
          </b-button>
        </div>
      </b-form>
    </b-modal>
  </b-container>
</template>

<script>
import NotificationRecipientSvc from '@/services/NotificationRecipientSvc';
import UserSvc from '@/services/UserSvc';
import apiCrudMixin from '@/mixins/apiCrudMixin';
import SearchableSelect from '@/components/master/SearchableSelect.vue';

const SEARCH_DEBOUNCE_MS = 400;

export default {
  name: 'NotificationRecipient',
  mixins: [apiCrudMixin],
  components: {
    SearchableSelect,
  },
  data() {
    return {
      addModalVisible: false,
      deleteModalVisible: false,
      editingId: null,
      form: {
        ntrcName: '',
        ntrcEmail: '',
        ntrcStatus: 1,
      },
      searchName: '',
      searchEmail: '',
      selectedUsers: [],
      searchTimer: null,
      selectedRecipient: {},
      statusOptions: [
        { value: 1, text: 'Active' },
        { value: 0, text: 'Inactive' },
      ],
      tableFields: [
        { key: 'ntrcId', label: 'ID', thStyle: { width: '80px' } },
        { key: 'ntrcName', label: 'Name' },
        { key: 'ntrcEmail', label: 'Email' },
        { key: 'ntrcStatus', label: 'Status', thStyle: { width: '110px' } },
        { key: 'ntrcCreatedTime', label: 'Created Time' },
        { key: 'ntrcUpdatedTime', label: 'Updated Time' },
        { key: 'actions', label: '', thStyle: { width: '100px' } },
      ],
    };
  },
  computed: {
    canSubmit() {
      if (!this.isEditing && this.selectedUsers.length) {
        return true;
      }
      return this.form.ntrcName && this.form.ntrcEmail;
    },
    emptyText() {
      return this.hasActiveSearch
        ? 'No recipient matches your search.'
        : 'No notification recipient data.';
    },
    hasActiveSearch() {
      return Boolean(this.searchName || this.searchEmail);
    },
    isEditing() {
      return this.editingId !== null;
    },
  },
  watch: {
    searchName: 'onSearchChanged',
    searchEmail: 'onSearchChanged',
    selectedUsers(users) {
      if (!this.isEditing && users && users.length) {
        this.form.ntrcName = '';
        this.form.ntrcEmail = '';
      }
    },
  },
  mounted() {
    this.fetchList();
  },
  beforeDestroy() {
    clearTimeout(this.searchTimer);
  },
  methods: {
    async createRecipientsFromUsers() {
      const uniqueUsers = this.selectedUsers.filter((user, index, arr) => arr.findIndex((it) => it.value === user.value) === index);
      const requests = uniqueUsers.map((user) => {
        const payload = {
          ntrcName: user.ntrcName || user.userName || user.value,
          ntrcEmail: user.ntrcEmail || user.value,
          ntrcStatus: this.form.ntrcStatus,
        };
        return NotificationRecipientSvc.create(payload);
      });
      await Promise.all(requests);
    },
    closeAddRecipientModal() {
      this.addModalVisible = false;
    },
    confirmDelete(recipient) {
      this.selectedRecipient = recipient;
      this.deleteModalVisible = true;
    },
    async createRecipient() {
      if (!this.isEditing && this.selectedUsers.length) {
        await this.runRequest('POST', () => this.createRecipientsFromUsers(), async () => {
          this.resetForm();
          this.addModalVisible = false;
          this.setMessage('Recipients saved from master users.');
          await this.fetchList(false);
        });
        return;
      }

      const payload = {
        ntrcName: this.form.ntrcName,
        ntrcEmail: this.form.ntrcEmail,
        ntrcStatus: this.form.ntrcStatus,
      };
      const method = this.isEditing ? 'PUT' : 'POST';
      const requestFn = this.isEditing
        ? () => NotificationRecipientSvc.update(this.editingId, payload)
        : () => NotificationRecipientSvc.create(payload);

      await this.runRequest(method, requestFn, async () => {
        this.resetForm();
        this.addModalVisible = false;
        this.setMessage(this.isEditing ? 'Recipient updated.' : 'Recipient saved.');
        await this.fetchList(false);
      });
    },
    async deleteRecipient() {
      const id = this.selectedRecipient.ntrcId;
      await this.runRequest(
        'DELETE',
        () => NotificationRecipientSvc.delete(id),
        async () => {
          this.deleteModalVisible = false;
          this.selectedRecipient = {};
          this.setMessage('Recipient deleted.');
          await this.fetchList(false);
        },
      );
    },
    async fetchList(showLoading = true) {
      const params = {
        page: this.pagination.page,
        max: this.pagination.itemPerPage,
      };
      if (this.searchName) {
        params['finds[ntrcName]'] = this.searchName;
      }
      if (this.searchEmail) {
        params['finds[ntrcEmail]'] = this.searchEmail;
      }

      await this.runRequest(
        'GET',
        () => NotificationRecipientSvc.get(params),
        null,
        showLoading,
      );
    },
    onSearchChanged() {
      clearTimeout(this.searchTimer);
      this.searchTimer = setTimeout(() => {
        this.pagination.page = 1;
        this.fetchList();
      }, SEARCH_DEBOUNCE_MS);
    },
    openAddRecipientModal() {
      this.resetForm();
      this.addModalVisible = true;
    },
    openEditRecipientModal(recipient) {
      this.editingId = recipient.ntrcId;
      this.form = {
        ntrcName: recipient.ntrcName,
        ntrcEmail: recipient.ntrcEmail,
        ntrcStatus: recipient.ntrcStatus,
      };
      this.addModalVisible = true;
    },
    resetForm() {
      this.editingId = null;
      this.selectedUsers = [];
      this.form = {
        ntrcName: '',
        ntrcEmail: '',
        ntrcStatus: 1,
      };
    },
    async searchMasterUsers(query) {
      const params = {
        page: 1,
        max: 25,
      };
      if (query) {
        params['finds[userId]'] = query;
      }

      const response = await UserSvc.getUsers({ params });
      const rows = Array.isArray(response && response.data && response.data.data)
        ? response.data.data
        : [];

      return rows.map((user) => {
        const email = user.userId || '-';
        const userName = user.usloUserName || user.userName || user.userId || '-';
        return {
          value: email,
          text: `${userName} - ${email}`,
          ntrcName: userName,
          ntrcEmail: email,
        };
      });
    },
  },
};
</script>

<style src="@/views/master/_apiPanel.css" scoped></style>
